import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Col, Row, Select, DatePicker, Spin, Empty, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../../config';
import { getUserToken } from '../../utils/helpers';
import { SummaryCard } from './DashboardStyle';
import { ThemeTable, ThemeTableCard } from '../Styles/Theme';
import LineGraph from './Charts/LineGraph';
import Heatmap from './Charts/Heatmap';
import InitialsAvatar from '../../Components/InitialsAvatar/InitialsAvatar';
import { CHART_PALETTE } from '../../utils/chartColors';

const { RangePicker } = DatePicker;

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return String(num);
};

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Parse "YYYY-MM-DD" as local time (not UTC) to avoid timezone day-shift bugs
const parseLocalDate = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Format a local Date as "YYYY-MM-DD" without UTC conversion
const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DATE_FILTER_OPTIONS = [
  { label: 'All Time', value: 'all' },
  { label: 'Today', value: 'today' },
  { label: 'Last 7 Days', value: '7days' },
  { label: 'Last 30 Days', value: '30days' },
  { label: 'Custom Range', value: 'custom' },
];

const UsageMonitoring = () => {
  const [dateFilter, setDateFilter] = useState('all');
  const [customRange, setCustomRange] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [error, setError] = useState(null);

  const fetchUsageStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter === 'today') {
        params.filter = 'day';
      } else if (dateFilter === '7days') {
        params.filter = 'week';
      } else if (dateFilter === '30days') {
        params.filter = 'month';
      } else if (dateFilter === 'custom' && customRange) {
        params.filter = 'custom';
        params.startDate = customRange[0].format('YYYY-MM-DD');
        params.endDate = customRange[1].format('YYYY-MM-DD');
      }

      const response = await axios.get(`${config.api.base_url}/chats/usage-stats`, {
        params,
        headers: { Authorization: 'Bearer ' + getUserToken() },
      });

      if (response.data?.data) {
        setData(response.data.data);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch usage stats:', err);
      setError('Failed to load usage data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [dateFilter, customRange]);

  useEffect(() => {
    if (dateFilter !== 'custom' || customRange) {
      fetchUsageStats();
    }
  }, [fetchUsageStats]);

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value !== 'custom') {
      setCustomRange(null);
    }
    setSelectedUsers([]);
    setTablePage(1);
  };

  const handleRangeChange = (dates) => {
    setCustomRange(dates);
    setSelectedUsers([]);
    setTablePage(1);
  };

  const allUsers = data?.users || [];

  const userOptions = useMemo(() => allUsers.map((u) => ({
    label: u.userName || u.userEmail || 'Unknown',
    value: u.userId,
  })), [allUsers]);

  const users = useMemo(() =>
    selectedUsers.length > 0
      ? allUsers.filter((u) => selectedUsers.includes(u.userId))
      : allUsers,
    [allUsers, selectedUsers]
  );

  // Rebuild dailyTrend from filtered users' dailyEntries
  const dailyTrend = useMemo(() => {
    if (selectedUsers.length === 0) return data?.dailyTrend || [];
    const trendMap = {};
    users.forEach((u) => {
      (u.dailyEntries || []).forEach((entry) => {
        if (!trendMap[entry.date]) {
          trendMap[entry.date] = { date: entry.date, messagesSent: 0, aiResponses: 0, responseWords: 0, tokensUsed: 0, chatsCreated: 0, chartsGenerated: 0, tablesGenerated: 0, projectsCreated: 0 };
        }
        trendMap[entry.date].messagesSent += entry.messagesSent || 0;
        trendMap[entry.date].aiResponses += entry.aiResponses || 0;
        trendMap[entry.date].responseWords += entry.responseWords || 0;
        trendMap[entry.date].tokensUsed += entry.tokensUsed || 0;
        trendMap[entry.date].chatsCreated += entry.chatsCreated || 0;
        trendMap[entry.date].chartsGenerated += entry.chartsGenerated || 0;
        trendMap[entry.date].tablesGenerated += entry.tablesGenerated || 0;
        trendMap[entry.date].projectsCreated += entry.projectsCreated || 0;
      });
    });
    return Object.values(trendMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [data, users, selectedUsers]);

  const totals = useMemo(() => users.reduce(
    (acc, u) => ({
      totalResponseWords: acc.totalResponseWords + (u.totalResponseWords || 0),
      totalMessagesSent: acc.totalMessagesSent + (u.totalMessagesSent || 0),
      totalChats: acc.totalChats + (u.totalChats || 0),
      totalTokensUsed: acc.totalTokensUsed + (u.totalTokensUsed || 0),
      chartsGenerated: acc.chartsGenerated + (u.chartsGenerated || 0),
      tablesGenerated: acc.tablesGenerated + (u.tablesGenerated || 0),
      totalProjects: acc.totalProjects + (u.totalProjects || 0),
      totalFilesUploaded: acc.totalFilesUploaded + (u.totalFilesUploaded || 0),
      totalFilesInProjects: acc.totalFilesInProjects + (u.totalFilesInProjects || 0),
      totalAiResponses: acc.totalAiResponses + (u.totalAiResponses || 0),
    }),
    {
      totalResponseWords: 0,
      totalMessagesSent: 0,
      totalChats: 0,
      totalTokensUsed: 0,
      chartsGenerated: 0,
      tablesGenerated: 0,
      totalProjects: 0,
      totalFilesUploaded: 0,
      totalFilesInProjects: 0,
      totalAiResponses: 0,
    }
  ), [users]);

  // --- Chart data ---

  // Heatmap: Usage by day of week (GitHub-style month labels)
  const { lastWeekKeys, heatmapXLabels, heatmapValues, heatmapMax, heatmapMsgMap, heatmapAiMap, heatmapWordsMap } = useMemo(() => {
    const _weekKeys = [];
    const _heatmapMsgMap = {};
    const _heatmapAiMap = {};
    const _heatmapWordsMap = {};
    dailyTrend.forEach((d) => {
      const date = parseLocalDate(d.date);
      const dayOfWeek = date.getDay();
      const monday = new Date(date);
      monday.setDate(monday.getDate() - ((dayOfWeek + 6) % 7));
      const weekKey = toLocalDateStr(monday);
      if (!_weekKeys.includes(weekKey)) {
        _weekKeys.push(weekKey);
      }
      const key = `${weekKey}-${dayOfWeek}`;
      _heatmapMsgMap[key] = (_heatmapMsgMap[key] || 0) + (d.messagesSent || 0);
      _heatmapAiMap[key] = (_heatmapAiMap[key] || 0) + (d.aiResponses || 0);
      _heatmapWordsMap[key] = (_heatmapWordsMap[key] || 0) + (d.responseWords || 0);
    });
    const _lastWeekKeys = _weekKeys.slice(-12);
    const _heatmapXLabels = _lastWeekKeys.map((k) => {
      const monday = parseLocalDate(k);
      const month = monday.getMonth();
      const dayOfMonth = monday.getDate();
      const weekOfMonth = Math.ceil(dayOfMonth / 7);
      return `${monthNames[month]} W${weekOfMonth}`;
    });
    const _heatmapValues = [];
    _lastWeekKeys.forEach((weekKey, xi) => {
      dayNames.forEach((_, yi) => {
        const key = `${weekKey}-${yi}`;
        const total = (_heatmapMsgMap[key] || 0) + (_heatmapAiMap[key] || 0);
        _heatmapValues.push([xi, yi, total || '-']);
      });
    });
    // Only compute max from displayed weeks (lastWeekKeys), not all weeks
    const _displayedTotals = _lastWeekKeys.flatMap((weekKey) =>
      dayNames.map((_, yi) => {
        const key = `${weekKey}-${yi}`;
        return (_heatmapMsgMap[key] || 0) + (_heatmapAiMap[key] || 0);
      })
    );
    const _heatmapMax = Math.max(..._displayedTotals, 1);
    return {
      lastWeekKeys: _lastWeekKeys,
      heatmapXLabels: _heatmapXLabels,
      heatmapValues: _heatmapValues,
      heatmapMax: _heatmapMax,
      heatmapMsgMap: _heatmapMsgMap,
      heatmapAiMap: _heatmapAiMap,
      heatmapWordsMap: _heatmapWordsMap,
    };
  }, [dailyTrend]);

  const heatmapTooltipFormatter = useCallback((params) => {
    const xi = params.data[0];
    const yi = params.data[1];
    const day = dayNames[yi] || '';
    const weekKey = lastWeekKeys[xi];
    const monday = parseLocalDate(weekKey);
    const offset = yi === 0 ? -1 : yi - 1;
    const cellDate = new Date(monday);
    cellDate.setDate(cellDate.getDate() + offset);
    const dateStr = `${monthNames[cellDate.getMonth()]} ${cellDate.getDate()}, ${cellDate.getFullYear()}`;
    const key = `${weekKey}-${yi}`;
    const msgs = heatmapMsgMap[key] || 0;
    const ai = heatmapAiMap[key] || 0;
    const words = heatmapWordsMap[key] || 0;
    return `<b>${day}, ${dateStr}</b><br/>Messages Sent: <b>${msgs}</b><br/>AI Responses: <b>${ai}</b><br/>Response Words: <b>${formatNumber(words)}</b>`;
  }, [lastWeekKeys, heatmapMsgMap, heatmapAiMap, heatmapWordsMap]);

  // LineGraph: Daily Usage Trend
  const { trendXLabels, trendSeries } = useMemo(() => ({
    trendXLabels: dailyTrend.map((d) => {
      const date = parseLocalDate(d.date);
      return `${monthNames[date.getMonth()]} ${date.getDate()}`;
    }),
    trendSeries: [
      { name: 'Messages Sent', data: dailyTrend.map((d) => d.messagesSent || 0), color: CHART_PALETTE.primary },
      { name: 'AI Responses', data: dailyTrend.map((d) => d.aiResponses || 0), color: CHART_PALETTE.secondary },
      { name: 'Response Words', data: dailyTrend.map((d) => d.responseWords || 0), color: CHART_PALETTE.accent2, yAxisIndex: 1 },
      { name: 'Chats Created', data: dailyTrend.map((d) => d.chatsCreated || 0), color: CHART_PALETTE.accent1 },
      { name: 'Projects Created', data: dailyTrend.map((d) => d.projectsCreated || 0), color: '#10B981' },
    ],
  }), [dailyTrend]);

  const trendTooltipFormatter = useCallback((params) => {
    if (!params || !params.length) return '';
    const idx = params[0].dataIndex;
    const d = dailyTrend[idx];
    if (!d) return '';
    const date = parseLocalDate(d.date);
    const dayName = dayNames[date.getDay()];
    const dateStr = `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    let html = `<b>${dayName}, ${dateStr}</b>`;
    params.forEach((p) => {
      const dot = `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px;"></span>`;
      html += `<br/>${dot}${p.seriesName}: <b>${formatNumber(p.value)}</b>`;
    });
    return html;
  }, [dailyTrend]);
  // Table columns
  const columns = [
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'userName',
      fixed: 'left',
      width: 200,
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <InitialsAvatar name={name || 'Unknown'} size={32} />
          <div>
            <div style={{ fontWeight: 500 }}>{name || 'Unknown'}</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{record.userEmail}</div>
          </div>
        </div>
      ),
      sorter: (a, b) => (a.userName || '').localeCompare(b.userName || ''),
    },
    {
      title: 'Organization',
      dataIndex: 'organizationName',
      key: 'organizationName',
      width: 150,
      render: (val) => val || '-',
      sorter: (a, b) => (a.organizationName || '').localeCompare(b.organizationName || ''),
    },
    {
      title: 'Messages',
      dataIndex: 'totalMessagesSent',
      key: 'totalMessagesSent',
      width: 100,
      sorter: (a, b) => (a.totalMessagesSent || 0) - (b.totalMessagesSent || 0),
      defaultSortOrder: 'descend',
      render: (val) => formatNumber(val || 0),
    },
    {
      title: 'AI Responses',
      dataIndex: 'totalAiResponses',
      key: 'totalAiResponses',
      width: 110,
      sorter: (a, b) => (a.totalAiResponses || 0) - (b.totalAiResponses || 0),
      render: (val) => formatNumber(val || 0),
    },
    {
      title: 'Words',
      dataIndex: 'totalResponseWords',
      key: 'totalResponseWords',
      width: 100,
      sorter: (a, b) => (a.totalResponseWords || 0) - (b.totalResponseWords || 0),
      render: (val) => formatNumber(val || 0),
    },
    {
      title: 'Tokens',
      dataIndex: 'totalTokensUsed',
      key: 'totalTokensUsed',
      width: 100,
      sorter: (a, b) => (a.totalTokensUsed || 0) - (b.totalTokensUsed || 0),
      render: (val) => formatNumber(val || 0),
    },
    {
      title: 'Chats',
      dataIndex: 'totalChats',
      key: 'totalChats',
      width: 80,
      sorter: (a, b) => (a.totalChats || 0) - (b.totalChats || 0),
      render: (val) => formatNumber(val || 0),
    },
    {
      title: 'Projects',
      dataIndex: 'totalProjects',
      key: 'totalProjects',
      width: 90,
      sorter: (a, b) => (a.totalProjects || 0) - (b.totalProjects || 0),
      render: (val) => formatNumber(val || 0),
    },
    {
      title: 'Files Uploaded',
      dataIndex: 'totalFilesUploaded',
      key: 'totalFilesUploaded',
      width: 120,
      sorter: (a, b) => (a.totalFilesUploaded || 0) - (b.totalFilesUploaded || 0),
      render: (val) => formatNumber(val || 0),
    },
    {
      title: 'Charts',
      dataIndex: 'chartsGenerated',
      key: 'chartsGenerated',
      width: 80,
      sorter: (a, b) => (a.chartsGenerated || 0) - (b.chartsGenerated || 0),
      render: (val) => formatNumber(val || 0),
    },
    {
      title: 'Tables',
      dataIndex: 'tablesGenerated',
      key: 'tablesGenerated',
      width: 80,
      sorter: (a, b) => (a.tablesGenerated || 0) - (b.tablesGenerated || 0),
      render: (val) => formatNumber(val || 0),
    },
  ];

  const summaryCards = [
    { title: 'Total Messages Sent', value: totals.totalMessagesSent, icon: '/images/dashboard-icon-4.svg' },
    { title: 'AI Responses Generated', value: totals.totalAiResponses, icon: '/images/dashboard-icon-2.svg' },
    { title: 'Response Words', value: totals.totalResponseWords, icon: '/images/dashboard-icon-3.svg' },
    { title: 'Chats Created', value: totals.totalChats, icon: '/images/dashboard-icon-4.svg' },
    { title: 'Projects Created', value: totals.totalProjects, icon: '/images/dashboard-icon-1.svg' },
  ];

  return (
    <Spin spinning={loading}>
      {/* Date Filter */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Select
            value={dateFilter}
            onChange={handleDateFilterChange}
            options={DATE_FILTER_OPTIONS}
            style={{ width: 160 }}
          />
          {dateFilter === 'custom' && (
            <RangePicker value={customRange} onChange={handleRangeChange} />
          )}
          <Select
            mode="multiple"
            allowClear
            placeholder="All Users"
            value={selectedUsers}
            onChange={(val) => { setSelectedUsers(val); setTablePage(1); }}
            options={userOptions}
            style={{ minWidth: 200, maxWidth: 400 }}
            maxTagCount={2}
            maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
          />
        </Col>
      </Row>

      {error ? (
        <Empty description={error} />
      ) : !data && !loading ? (
        <Empty description="No usage data available" />
      ) : (
        <Row gutter={[16, 16]} className="mb-6" style={{ display: 'flex', flexWrap: 'wrap' }}>
          {/* Row 1: 5 stat cards (equal width) */}
          {summaryCards.map((card, index) => (
            <Col xs={12} sm={12} md={8} key={`card-${index}`} style={{ flex: '1 1 0', maxWidth: '20%', minWidth: 160 }}>
              <SummaryCard className="small">
                <div className="smallCard">
                  <img src={card.icon} alt={card.title} />
                  <div className="smallCardTitle my-5">{card.title}</div>
                  <div className="smallCardDescription">{formatNumber(card.value)}</div>
                </div>
              </SummaryCard>
            </Col>
          ))}

          {/* Row 2: Heatmap full width */}
          <Col xs={24}>
            <Heatmap
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Weekly Activity by Day
                  <Tooltip title="Shows combined messages sent and AI responses for each day of the week across recent weeks. Darker cells mean higher activity. Hover to see the full breakdown per day.">
                    <InfoCircleOutlined style={{ fontSize: 13, color: '#9CA3AF', cursor: 'pointer' }} />
                  </Tooltip>
                </span>
              }
              xLabels={heatmapXLabels}
              yLabels={dayNames}
              data={heatmapValues}
              valueRange={{ min: 0, max: heatmapMax }}
              tooltipFormatter={heatmapTooltipFormatter}
              chartHeight={360}
            />
          </Col>

          {/* Row 3: Trend full width */}
          <Col xs={24}>
            <LineGraph
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Daily Usage Trends
                  <Tooltip title="Tracks daily platform metrics over time — messages sent, AI responses, response word count, chats created, and projects created. Click any legend item to show/hide that metric.">
                    <InfoCircleOutlined style={{ fontSize: 13, color: '#9CA3AF', cursor: 'pointer' }} />
                  </Tooltip>
                </span>
              }
              xLabels={trendXLabels}
              series={trendSeries}
              tooltipFormatter={trendTooltipFormatter}
              chartHeight={360}
            />
          </Col>

          {/* Row 4: Table */}
          <Col xs={24}>
            <ThemeTableCard>
              <ThemeTable
                columns={columns}
                dataSource={users.map((u, i) => ({ ...u, key: u.userId || i }))}
                pagination={{
                  current: tablePage,
                  pageSize: tablePageSize,
                  total: users.length,
                  showSizeChanger: true,
                  showQuickJumper: false,
                  showTotal: (total) => `Total ${total} users`,
                  onChange: (page, size) => { setTablePage(page); setTablePageSize(size); },
                }}
                scroll={{ x: 1200 }}
              />
            </ThemeTableCard>
          </Col>
        </Row>
      )}
    </Spin>
  );
};

export default UsageMonitoring;
